import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Divider,
  Paper,
} from '@mui/material';
import {
  Button,
  Drawer,
  ListItemButton,
  NameEmailAvatar,
} from '../../../../../../../ui';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { CustomSelect } from '../../../../../../../ui/custom_react_select/custom_select';
import { useDebounce } from '../../../../../../../utils/hooks/useDebounce';
import { IInterviewResult } from '../../Results/Hooks';
import { StatusToggle } from '../Components';
import { useInviteCandidates } from './Hooks';

export interface Applicant {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  applicantStatus?: string;
  interviewType?: 'Virtual' | 'In-Person';
}

export interface IInviteCandidates {
  isOpen: boolean;
  onCloseDrawer: () => void;
  interviewRoundID: string;
  onInvitesSent: () => Promise<void>;
  isNotFirstRound?: boolean;
  results: IInterviewResult[];
}

const APPLICANT_STATUSES = [
  'Shortlisted',
  'New',
  'Outstanding',
  'Suitable',
  'Waitlisted',
  'Interview',
  'Success',
  'Offer-Extended',
  'Offer-Declined',
];

export const InviteCandidates: React.FC<IInviteCandidates> = ({
  isOpen,
  onCloseDrawer,
  interviewRoundID,
  onInvitesSent,
  isNotFirstRound,
  results,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  const usingPreviousRoundResults = !!isNotFirstRound && results?.length > 0;

  const [candidateTypes, setCandidateTypes] = React.useState<
    Record<string, 'Virtual' | 'In-Person'>
  >({});

  const {
    interviewApplicants,
    selectedCandidates,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    sendAllInvites,
    newSelections,
    setSearchObject,
    pageNumber,
    setPageNumber,
    totalPages,
    totalRecords,
  } = useInviteCandidates({ interviewRoundID, isOpen, candidateTypes });

  const [localName, setLocalName] = React.useState('');
  const [localEmail, setLocalEmail] = React.useState('');
  const [applicantStatus, setApplicantStatus] = React.useState<string[]>(
    usingPreviousRoundResults ? [] : ['Shortlisted'],
  );

  const debouncedName = useDebounce(localName, 500);
  const debouncedEmail = useDebounce(localEmail, 500);

  React.useEffect(() => {
    setSearchObject({
      name: debouncedName,
      email: debouncedEmail,
      interviewStatus: applicantStatus,
    });
  }, [debouncedName, debouncedEmail, applicantStatus, setSearchObject]);

  const filters = {
    name: localName,
    email: localEmail,
    applicantStatus,
  };

  const applicantStatusOptions = APPLICANT_STATUSES.map((status) => ({
    label: status,
    value: status,
  }));

  const handleStatusChange = (selected: any) => {
    setApplicantStatus(selected ? selected.map((item: any) => item.value) : []);
  };

  const handleSend = async () => {
    try {
      await sendAllInvites();
      onInvitesSent();
    } catch (e) {
      console.error(e);
    }
  };

  const handleTypeChange = (
    candidateId: string,
    value: 'Virtual' | 'In-Person',
  ) => {
    setCandidateTypes((prev) => ({ ...prev, [candidateId]: value }));
  };

  const getTypeForCandidate = (id: string): string => {
    return candidateTypes[id] || 'Virtual';
  };

  React.useEffect(() => {
    setApplicantStatus(usingPreviousRoundResults ? [] : ['Shortlisted']);
  }, [usingPreviousRoundResults]);

  const filteredApplicants = React.useMemo(() => {
    if (!usingPreviousRoundResults) return interviewApplicants;

    const resultsAsCandidates = results
      .filter((result) => (
        result.user && result.user.email && result.status === 'Completed'
      ))
      .map((result) => ({
        id: result.jobApplicantId || result.user?.id || '',
        email: result.user?.email || '',
        firstName: result.user?.firstName || '',
        lastName: result.user?.lastName || '',
        applicantStatus:
          result.interviewApplicantStatus || result.status || 'Completed',
        interviewType: (result.interviewType === 'In-Person'
          ? 'In-Person'
          : 'Virtual') as 'Virtual' | 'In-Person',
        avatar: undefined,
      })) as Applicant[];

    const selectedIds = new Set(
      selectedCandidates.map((candidate) => candidate.id),
    );
    const selectedEmails = new Set(
      selectedCandidates.map((candidate) => candidate.email.toLowerCase()),
    );

    return resultsAsCandidates.filter((a) => {
      if (selectedIds.has(a.id) || selectedEmails.has(a.email.toLowerCase())) {
        return false;
      }

      const fullName = (a.firstName + ' ' + a.lastName).toLowerCase();
      const emailMatch = filters.email
        ? a.email.toLowerCase().includes(filters.email.toLowerCase())
        : true;
      const nameMatch = filters.name
        ? fullName.includes(filters.name.toLowerCase())
        : true;
      const statusMatch =
        filters.applicantStatus.length === 0 ||
        (a.applicantStatus &&
          filters.applicantStatus.some(
            (s) => s.toLowerCase() === a.applicantStatus!.toLowerCase(),
          ));

      return emailMatch && nameMatch && statusMatch;
    });
  }, [
    usingPreviousRoundResults,
    interviewApplicants,
    results,
    selectedCandidates,
    filters.email,
    filters.name,
    filters.applicantStatus,
  ]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      title={t(SI.candidates.invite.drawerTitle)}
      width='w-full md:w-[600px]'
      footerContent={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2.5, width: '100%' }}>
          <Button label={t(SI.candidates.invite.cancel)} onClick={onCloseDrawer} variant='secondary' />
          <Button
            label={t(SI.candidates.invite.sendInvitations)}
            onClick={handleSend}
            variant='primary'
            disabled={newSelections.length === 0}
          />
        </Box>
      }
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                {t(SI.candidates.invite.availableCandidates)}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {t(SI.candidates.invite.subtitle)}
              </Typography>
            </Box>
            {interviewApplicants.length > 0 && (
              <Button
                variant='link'
                label={t(SI.candidates.invite.selectAll)}
                onClick={handleSelectAll}
              />
            )}
          </Box>

          {/* Filter Controls */}
          {(!isNotFirstRound || results?.length > 0) && (
            <Paper
              variant='outlined'
              sx={{
                p: 2.5,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                gap: 2,
                bgcolor: 'action.hover',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 180, flex: 1 }}>
                <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t(SI.candidates.invite.filterName)}
                </Typography>
                <TextField
                  size='small'
                  placeholder={t(SI.candidates.invite.searchByName)}
                  value={localName}
                  onChange={(e) => setLocalName(e.target.value)}
                  sx={{ bgcolor: 'background.paper' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 180, flex: 1 }}>
                <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t(SI.candidates.invite.filterEmail)}
                </Typography>
                <TextField
                  size='small'
                  placeholder={t(SI.candidates.invite.searchByEmail)}
                  value={localEmail}
                  onChange={(e) => setLocalEmail(e.target.value)}
                  sx={{ bgcolor: 'background.paper' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 180, flex: 1 }}>
                <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t(SI.candidates.invite.filterStatus)}
                </Typography>
                <CustomSelect
                  name='applicantStatus'
                  value={applicantStatus.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  options={applicantStatusOptions}
                  onChange={handleStatusChange}
                  isMulti
                  clearable
                  placeholder={t(SI.candidates.invite.filterByStatus)}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Button
                  type='button'
                  onClick={() => {
                    setLocalName('');
                    setLocalEmail('');
                    setApplicantStatus([]);
                  }}
                  label={t(SI.candidates.invite.clearFilters)}
                  variant='secondary'
                />
              </Box>
            </Paper>
          )}
        </Box>

        {filteredApplicants.length === 0 ? (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t(SI.candidates.invite.noCandidates)}
          </Typography>
        ) : (
          <Stack component='ul' spacing={1} sx={{ p: 0, m: 0, listStyle: 'none' }}>
            {filteredApplicants.map((app) => (
              <ListItemButton key={app.id} onClick={() => handleSelect(app)}>
                <NameEmailAvatar
                  email={app.email}
                  name={`${app.firstName} ${app.lastName}`}
                  avatar={app.avatar || ''}
                />
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {results.find((r) => r.jobApplicantId === app.id)?.aiOverallScore?.score
                      ? `${results.find((r) => r.jobApplicantId === app.id)?.aiOverallScore?.score}%`
                      : ''}
                  </Typography>
                  <Typography variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {app.applicantStatus}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </Stack>
        )}

        {!usingPreviousRoundResults && totalRecords > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pt: 1 }}>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {totalRecords} candidate{totalRecords !== 1 ? 's' : ''} found
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                type='button'
                variant='secondary'
                label='Prev'
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              />
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                Page {pageNumber} of {totalPages}
              </Typography>
              <Button
                type='button'
                variant='secondary'
                label='Next'
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              />
            </Box>
          </Box>
        )}

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            {t(SI.candidates.invite.selectedCandidates)}
          </Typography>
          {selectedCandidates.length > 0 && (
            <Button
              variant='link'
              label={t(SI.candidates.invite.removeAll)}
              onClick={handleRemoveAll}
            />
          )}
        </Box>

        {selectedCandidates.length === 0 ? (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t(SI.candidates.invite.noSelected)}
          </Typography>
        ) : (
          <Stack component='ul' spacing={1} sx={{ p: 0, m: 0, listStyle: 'none' }}>
            {selectedCandidates.map((cand) => (
              <ListItemButton
                key={cand.id}
                isSelected
                onClick={() => {}}
                onRemove={() => handleRemove(cand)}
              >
                <Stack spacing={1} sx={{ borderBottom: '1px solid', borderColor: 'divider', width: '100%', pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <NameEmailAvatar
                      email={cand.email}
                      name={`${cand.firstName} ${cand.lastName}`}
                      avatar={cand.avatar || ''}
                    />
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {results.find((r) => r.jobApplicantId === cand.id)?.aiOverallScore?.score
                        ? `${results.find((r) => r.jobApplicantId === cand.id)?.aiOverallScore?.score}%`
                        : ''}
                    </Typography>
                  </Box>
                  <StatusToggle
                    options={['Virtual', 'In-Person']}
                    value={getTypeForCandidate(cand.id)}
                    onChange={(val) =>
                      handleTypeChange(cand.id, val as 'Virtual' | 'In-Person')
                    }
                  />
                </Stack>
              </ListItemButton>
            ))}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};

export default InviteCandidates;